import React, { useState, useRef } from 'react';
import {
  Button,
  Badge,
  Menu,
  Group,
  Box,
  rem,
  Input,
  Flex,
  Checkbox,
  ActionIcon
} from '@mantine/core';
import { FaPlus } from 'react-icons/fa6';
import { IoIosArrowDown } from 'react-icons/io';

// Extracted styles
const containerStyles = {
  border: '1px solid #ced4da',
  borderRadius: 4,
  padding: '4px 8px',
  height: 30.2,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  background: '#fff',
  minWidth: 320,
  overflow: 'hidden'
};

const groupStyles = {
  flexWrap: 'nowrap',
  overflow: 'hidden'
};

const inputStyles = {
  input: {
    border: 'none',
    outline: 'none',
    padding: 0
  }
};

const buttonStyles = {
  minWidth: 28
};

const flexStyles = {
  overflow: 'scroll'
};

// Extracted functions
const handleInsertValue = (insertValue, data, value, onChange, props, setInsertValue, inputRef) => {
  const trimmed = insertValue?.trim();
  if (!trimmed) return;
  // If not already in data, add to data
  if (!data.includes(trimmed)) {
    if (typeof props.onDataChange === 'function') {
      props.onDataChange([...data, trimmed]);
    }
  }
  // Add to selected if not already selected
  if (!value.includes(trimmed)) {
    onChange([...value, trimmed]);
  }
  setInsertValue('');
  setTimeout(() => {
    inputRef.current?.focus();
  }, 0);
};

const handleInputChange = (e, setMenuOpened, setInsertValue, inputRef) => {
  setMenuOpened(true);
  setInsertValue(e.currentTarget.value);
  setTimeout(() => {
    inputRef.current?.focus();
  }, 100);
};

const handleInputKeyDown = (e, handleInsert) => {
  if (e.key === 'Enter') {
    handleInsert();
  }
};

const handleInputFocus = e => {
  e.preventDefault();
  e.stopPropagation();
};

const handleActionIconClick = (e, setMenuOpened) => {
  e.stopPropagation();
  e.preventDefault();
  setMenuOpened(o => !o);
};

const handleCheckboxChange = (el, value, onChange) => {
  if (value.includes(el)) onChange(value.filter(e => e !== el));
  else onChange([...value, el]);
};

const renderBadges = _value => {
  const value = _value.filter(el => el);

  if (!value || value.length === 0) return null;
  if (value.length === 1) {
    return (
      <Badge size="sm" w={90} bg="#F1F3F5" radius={4} fw={400}>
        {value[0]}
      </Badge>
    );
  }
  if (value.length === 2) {
    return (
      <>
        <Badge size="sm" w={90} bg="#F1F3F5" radius={4} fw={400}>
          {value[0]}
        </Badge>
        <Badge size="sm" w={90} bg="#F1F3F5" radius={4} fw={400}>
          {value[1]}
        </Badge>
      </>
    );
  }
  // More than 2
  return (
    <>
      <Badge size="sm" w={90} bg="#F1F3F5" radius={4} fw={400}>
        {value[0]}
      </Badge>
      <Badge
        tt="lowercase"
        size="sm"
        w={90}
        bg="#F1F3F5"
        radius={4}
        fw={400}
      >{`+${value.length - 1} more`}</Badge>
    </>
  );
};

const MultiSelectWithInsert = ({
  id,
  data = [],
  value = [],
  onChange,
  placeholder = 'Select options',
  insertPlaceholder = 'Add new option',
  manualValues = [],
  ...props
}) => {
  const [menuOpened, setMenuOpened] = useState(false);
  const [insertValue, setInsertValue] = useState('');
  const [statedManualValues, setStatedManualValues] = useState([...(manualValues || [])]);
  const inputRef = useRef();

  const onChangeManualValues = val => {
    setStatedManualValues(prev => {
      return [...new Set([...prev, val])];
    });
  };

  // Handle insert new value
  const handleInsert = () => {
    if (!insertValue?.trim()) {
      inputRef.current?.focus();
      return;
    }
    handleInsertValue(insertValue, data, value, onChange, props, setInsertValue, inputRef);
    onChangeManualValues(insertValue);
  };

  const mutatedValues = new Set(
    [...statedManualValues, ...data]?.filter(
      el => el && el?.toLowerCase().includes(insertValue?.toLowerCase())
    )
  );

  return (
    <Menu
      id={id ? `${id}-menu` : 'grid-table-multi-select-menu'}
      opened={menuOpened}
      onChange={setMenuOpened}
      width={300}
      withinPortal
      closeOnItemClick={false}
      position="bottom-start"
      offset={rem(2)}
    >
      <Menu.Target>
        <Box
          id={id ? `${id}-trigger` : 'grid-table-multi-select-trigger'}
          onClick={() => setMenuOpened(o => !o)}
          pr={0}
          style={containerStyles}
        >
          {/* Badges and input in one box */}
          {value.length > 0 && (
            <Group spacing={2} gap={2} align="center" style={groupStyles}>
              {renderBadges(value)}{' '}
            </Group>
          )}

          <Input
            id={id ? `${id}-input` : 'grid-table-multi-select-input'}
            ref={inputRef}
            value={insertValue}
            onChange={e => handleInputChange(e, setMenuOpened, setInsertValue, inputRef)}
            placeholder={insertPlaceholder}
            size="xs"
            flex={1}
            styles={inputStyles}
            onKeyDown={e => handleInputKeyDown(e, handleInsert)}
            onFocus={handleInputFocus}
          />

          <ActionIcon
            id={id ? `${id}-toggle` : 'grid-table-multi-select-toggle'}
            variant="transparent"
            onClick={e => handleActionIconClick(e, setMenuOpened)}
          >
            <IoIosArrowDown />
          </ActionIcon>
        </Box>
      </Menu.Target>
      <Menu.Dropdown id={id ? `${id}-dropdown` : 'grid-table-multi-select-dropdown'}>
        <Box
          id={id ? `${id}-dropdown-content` : 'grid-table-multi-select-dropdown-content'}
          px="xs"
          py="xs"
        >
          {/* Add New Value input and button at the top */}
          <Flex align="center" gap={4}>
            <Button
              id={id ? `${id}-add` : 'grid-table-multi-select-add'}
              size="sm"
              variant="transparent"
              px={6}
              bg="transparent"
              style={buttonStyles}
              onClick={handleInsert}
              c="blue.6"
              leftSection={<FaPlus size={12} />}
            >
              Add unlisted format
            </Button>
          </Flex>

          <Flex direction="column" mah={200} gap={8} style={flexStyles}>
            {[...(mutatedValues || [])]?.map((el, idx) => (
              <Checkbox
                id={id ? `${id}-option-${idx}` : `grid-table-multi-select-option-${idx}`}
                key={idx}
                w="100%"
                checked={value?.includes(el)}
                label={el}
                onChange={() => handleCheckboxChange(el, value, onChange)}
              />
            ))}
          </Flex>
        </Box>
      </Menu.Dropdown>
    </Menu>
  );
};

export default MultiSelectWithInsert;
